/**
 * Theme toggle, shared by the public shell and the admin app (both esbuild
 * entry points import this one module rather than duplicating the logic).
 *
 * Three states, cycled by the toggle button: system (default, no override),
 * light, dark. The choice is read synchronously by an inline script in
 * index.html/admin.html <head>, before app.css is applied, so there is no
 * flash of the wrong theme on load; this module only needs to wire up the
 * button and keep it and the meta theme-color tags in sync afterwards.
 */

export type ThemePref = "system" | "light" | "dark";

const KEY = "theme";
const ORDER: ThemePref[] = ["system", "light", "dark"];
const LABEL: Record<ThemePref, string> = { system: "Auto", light: "Light", dark: "Dark" };

// Kept in sync with :root's --bg in app.css; duplicated rather than read via
// getComputedStyle to avoid a layout read on every toggle for two colours
// that essentially never change.
const BG = { light: "#f3ead2", dark: "#0e1424" };

export function currentPref(): ThemePref {
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" ? v : "system";
}

function applyTheme(pref: ThemePref): void {
  if (pref === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = pref;

  // Two theme-color metas, one per prefers-color-scheme value: forcing both
  // to the same content means whichever one the browser matches against the
  // real OS setting still shows the right colour when overridden.
  const light = document.querySelector('meta[name="theme-color"][media*="light"]');
  const dark = document.querySelector('meta[name="theme-color"][media*="dark"]');
  if (light && dark) {
    const [lc, dc] = pref === "system" ? [BG.light, BG.dark] : [BG[pref], BG[pref]];
    light.setAttribute("content", lc);
    dark.setAttribute("content", dc);
  }
}

/** Wires up a theme toggle button already in the page (id="theme-toggle"). */
export function initThemeToggle(): void {
  applyTheme(currentPref());
  const btn = document.getElementById("theme-toggle");
  if (!btn) return;
  const render = () => { btn.textContent = LABEL[currentPref()]; };
  render();
  btn.addEventListener("click", () => {
    const next = ORDER[(ORDER.indexOf(currentPref()) + 1) % ORDER.length];
    localStorage.setItem(KEY, next);
    applyTheme(next);
    render();
  });
}
